/*
 * HC - Product page redesign (B2B) - 2026-09-24
 *
 * Progressive behaviour for the redesigned main product page:
 * snippets/product-template.liquid rendered by sections/main-product.liquid
 * with ib_pdp true (root carries [data-ibp-root]). Loaded deferred from that
 * section, after theme.js.
 *
 * theme.Product (assets/theme.js) still owns variants, price, SKU, quantity
 * and the product form. This module only adds:
 *   - copy buttons on the SKU / UPC specs, and the UPC row's variant updates
 *   - the selected value beside each option label ("Volume  20 Volume")
 *   - the line total on the add-to-cart buttons (unit price x quantity)
 *   - the "already in your cart" line
 *   - the shipping countdown ("Order within 2 hrs 14 mins to ship today")
 *   - the sticky buy bar, which proxies the real quantity buttons and submits
 *     the real form (so AjaxProduct's access gate runs)
 *   - the order table's filter, in-cart highlight and cart summary; its
 *     steppers are collection-atc.js quick-add markup, so that module writes
 *     the cart and shows the toast, exactly as on the collection grid
 *
 * It only ever READS theme hooks ([data-add-to-cart], .js-qty__*,
 * [data-product-price] ...) and never reuses them on its own markup:
 * theme.js and the layout's [ST] helpers look those up by first match.
 * Variant changes come from the root's own `variantChange` event, not the
 * document-wide `variant:change`, which also fires for quick shop modals.
 */
(function() {
  'use strict';

  var STICKY_RECOVERY_MS = 30000;
  var COPIED_MS = 1600;

  var MINUTE_MS = 60000;
  var HOUR_MS = 3600000;
  var DAY_MS = 86400000;
  // Edmonton time as a fixed UTC-6: Alberta no longer changes clocks (IANA
  // tzdata 2026c), and a browser with older zone data would still fall back
  // to UTC-7 in November and move the cutoff an hour.
  var SHIP_TIME_ZONE = 'Etc/GMT+6';
  var SHIP_CUTOFF_HOUR = 12;
  var SHIP_LOOKAHEAD_DAYS = 60;
  var WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];

  var SELECTORS = {
    root: '[data-ibp-root]',
    cartJson: '[data-ibp-cart]',

    crumbBack: '[data-ibp-crumb-back]',

    copy: '[data-ibp-copy]',
    copied: '[data-ibp-copied]',
    chip: '[data-ibp-chip]',
    chipValue: '[data-ibp-chip-value]',
    barcode: '[data-ibp-barcode]',
    barcodeValue: '[data-ibp-barcode-value]',

    inCart: '[data-ibp-incart]',
    inCartCount: '[data-ibp-incart-count]',
    inCartView: '[data-ibp-incart-view]',

    picker: '[data-ibp-picker]',
    total: '[data-ibp-total]',

    ship: '[data-ibp-ship]',
    shipText: '[data-ibp-ship-text]',
    shipConfig: '[data-ibp-ship-config]',
    stockBlock: '[data-ibp-stock]',
    storeStock: '.stofind-find-in-store-button',

    sticky: '[data-ibp-sticky]',
    stickyAnchor: '[data-ibp-sticky-anchor]',
    stickyAdd: '[data-ibp-sticky-add]',
    stickyAddText: '[data-ibp-sticky-add-text]',
    stickyPrice: '[data-ibp-sticky-price]',
    stickyVariant: '[data-ibp-sticky-variant]',
    stickyVariantText: '[data-ibp-sticky-variant-text]',
    stickyThumb: '[data-ibp-sticky-thumb]',
    stickyQty: '[data-ibp-sticky-qty]',
    stickyQtyValue: '[data-ibp-sticky-qty-value]',
    stickyMinus: '[data-ibp-sticky-minus]',
    stickyPlus: '[data-ibp-sticky-plus]',

    table: '[data-ibp-table]',
    tableTitle: '[data-ibp-table-title]',
    jump: '[data-ibp-jump]',
    row: '[data-ibp-row]',
    tableFilter: '[data-ibp-table-filter]',
    tableEmpty: '[data-ibp-table-empty]',
    tableFooter: '[data-ibp-table-footer]',
    tableCount: '[data-ibp-table-count]',
    tableSubtotal: '[data-ibp-table-subtotal]',
    tableView: '[data-ibp-table-view]',

    // collection-atc.js stepper (it owns the value): read only
    rowInput: '.cg-qty-input',

    // theme.js hooks: read only
    atc: '[data-add-to-cart]',
    atcText: '[data-add-to-cart-text]',
    productForm: '.product-single__form',
    price: '[data-product-price]',
    qtyInput: '.js-qty__num',
    qtyMinus: '.js-qty__adjust--minus',
    qtyPlus: '.js-qty__adjust--plus',
    qtyWrapper: '.js-qty__wrapper',
    variantWrapper: '.variant-wrapper',
    variantLabel: '.variant__label',
    variantLabelInfo: '.variant__label-info',
    optionValue: '[data-ibp-option-value]',
    optionWrap: '.variant-input-wrap'
  };

  /*==========================================================================
    Pure helpers (exported for tests)
  ==========================================================================*/

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function toInt(value, fallback) {
    var number = parseInt(value, 10);
    if (isNaN(number)) {
      return fallback === undefined ? 0 : fallback;
    }
    return number;
  }

  // Quantity per variant id, summed across lines: promo, gift and bundle
  // lines carry properties, so one variant can sit on several lines.
  function cartQuantities(cart) {
    var map = {};
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    items.forEach(function(item) {
      var id = item && (item.variant_id || item.id);
      if (!id) {
        return;
      }
      var key = String(id);
      map[key] = (map[key] || 0) + Math.max(0, toInt(item.quantity, 0));
    });
    return map;
  }

  // Cart line totals per variant id, in cents, summed like cartQuantities.
  function cartTotals(cart) {
    var map = {};
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    items.forEach(function(item) {
      var id = item && (item.variant_id || item.id);
      if (!id) {
        return;
      }
      var total = item.final_line_price;
      if (typeof total !== 'number') {
        total = typeof item.line_price === 'number'
          ? item.line_price
          : toInt(item.price, 0) * Math.max(0, toInt(item.quantity, 0));
      }
      var key = String(id);
      map[key] = (map[key] || 0) + Math.max(0, toInt(total, 0));
    });
    return map;
  }

  // Units of these variants in the cart and what their lines add up to.
  function cartSummary(ids, quantities, totals) {
    var count = 0;
    var subtotal = 0;
    (ids || []).forEach(function(id) {
      count += (quantities && quantities[id]) || 0;
      subtotal += (totals && totals[id]) || 0;
    });
    return { count: count, subtotal: subtotal };
  }

  function formatMoney(cents) {
    var theme = window.theme;
    if (theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat);
    }
    return '$' + (toInt(cents, 0) / 100).toFixed(2);
  }

  function imageUrl(src, width) {
    if (!src) {
      return '';
    }
    var url = String(src);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function parseJson(el) {
    if (!el) {
      return null;
    }
    try {
      return JSON.parse(el.textContent || '');
    } catch (error) {
      return null;
    }
  }

  // Cart state carried by a cart event. Count-only and optimistic events
  // carry none, and must not reset the counts.
  function eventCart(evt) {
    var theme = window.theme;
    var detail = evt && evt.detail;
    if (!detail || !theme || !theme.cart || typeof theme.cart.getCartState !== 'function') {
      return null;
    }
    return theme.cart.getCartState(detail);
  }

  // Back / Forward can also rebuild the page from the HTTP cache (no
  // pageshow.persisted), with the cart JSON it was first served with.
  function historyLoad() {
    var perf = window.performance;
    if (!perf) {
      return false;
    }
    if (typeof perf.getEntriesByType === 'function') {
      var entry = perf.getEntriesByType('navigation')[0];
      if (entry && entry.type) {
        return entry.type === 'back_forward';
      }
    }
    return Boolean(perf.navigation && perf.navigation.type === 2);
  }

  function challengeActive() {
    var cart = window.theme && window.theme.cart;
    return Boolean(cart && typeof cart.isChallengeActive === 'function' && cart.isChallengeActive());
  }

  function prefersReducedMotion() {
    return typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // execCommand is deprecated but is still the only copy path on iOS Safari
  // in a non-secure context and on older Android WebViews (as ib-cart-save.js).
  function legacyCopy(value) {
    if (!document.execCommand || !document.createElement) {
      return false;
    }
    try {
      var field = document.createElement('textarea');
      field.value = value;
      field.setAttribute('readonly', 'readonly');
      document.body.appendChild(field);
      if (typeof field.select === 'function') {
        field.select();
      }
      var copied = document.execCommand('copy');
      document.body.removeChild(field);
      return Boolean(copied);
    } catch (error) {
      return false;
    }
  }

  function copyText(value, done) {
    var clipboard = window.navigator && window.navigator.clipboard;
    if (clipboard && typeof clipboard.writeText === 'function') {
      clipboard.writeText(value).then(function() {
        done(true);
      }, function() {
        done(legacyCopy(value));
      });
      return;
    }
    done(legacyCopy(value));
  }

  /*--------------------------------------------------------------------------
    Shipping cutoff: "Order within 2 hrs 14 mins to ship today"
  --------------------------------------------------------------------------*/

  // Calendar days count whole days since 1970-01-01 (UTC), so day
  // arithmetic never meets a daylight-saving change.
  function dayNumber(year, month, day) {
    return Math.round(Date.UTC(year, month - 1, day) / DAY_MS);
  }

  function weekday(day) {
    return new Date(day * DAY_MS).getUTCDay();
  }

  function isWeekend(day) {
    var dow = weekday(day);
    return dow === 0 || dow === 6;
  }

  function nthMonday(year, month, n) {
    var first = dayNumber(year, month, 1);
    return first + (8 - weekday(first)) % 7 + (n - 1) * 7;
  }

  // Anonymous Gregorian algorithm.
  function easterSunday(year) {
    var a = year % 19;
    var b = Math.floor(year / 100);
    var c = year % 100;
    var d = Math.floor(b / 4);
    var e = b % 4;
    var f = Math.floor((b + 8) / 25);
    var g = Math.floor((b - f + 1) / 3);
    var h = (19 * a + b - d - g + 15) % 30;
    var i = Math.floor(c / 4);
    var k = c % 4;
    var l = (32 + 2 * e + 2 * i - h - k) % 7;
    var m = Math.floor((a + 11 * h + 22 * l) / 451);
    var n = h + l - 7 * m + 114;
    return dayNumber(year, Math.floor(n / 31), n % 31 + 1);
  }

  // Holidays that are never shipping days, as listed in the theme setting
  // group "IB Shipping cutoff". New Year's, Canada Day, Christmas and Boxing
  // Day move to the next free weekday when they land on a weekend.
  var holidayCache = {};
  function holidays(year) {
    if (holidayCache[year]) {
      return holidayCache[year];
    }
    var days = [];
    var add = function(day, movesOffWeekend) {
      while (movesOffWeekend && (isWeekend(day) || days.indexOf(day) !== -1)) {
        day += 1;
      }
      days.push(day);
    };
    var may24 = dayNumber(year, 5, 24);
    add(dayNumber(year, 1, 1), true); // New Year's Day
    add(nthMonday(year, 2, 3)); // Family Day
    add(easterSunday(year) - 2); // Good Friday
    add(may24 - (weekday(may24) + 6) % 7); // Victoria Day: Monday before May 25
    add(dayNumber(year, 7, 1), true); // Canada Day
    add(nthMonday(year, 8, 1)); // Heritage Day
    add(nthMonday(year, 9, 1)); // Labour Day
    add(nthMonday(year, 10, 2)); // Thanksgiving
    add(dayNumber(year, 11, 11)); // Remembrance Day
    add(dayNumber(year, 12, 25), true); // Christmas Day
    add(dayNumber(year, 12, 26), true); // Boxing Day
    holidayCache[year] = days;
    return days;
  }

  // Every YYYY-MM-DD in the "Other days we don't ship" setting.
  function closedDays(text) {
    return (String(text || '').match(/\d{4}-\d{2}-\d{2}/g) || []).map(function(date) {
      var parts = date.split('-');
      return dayNumber(toInt(parts[0], 0), toInt(parts[1], 0), toInt(parts[2], 0));
    });
  }

  function isShipDay(day, closed) {
    var year = new Date(day * DAY_MS).getUTCFullYear();
    return !isWeekend(day) && holidays(year).indexOf(day) === -1 && closed.indexOf(day) === -1;
  }

  // Wall-clock date and time in timeZone at an instant. Throws a RangeError
  // for a time zone the browser doesn't know.
  var zoneFormats = {};
  function zonedParts(ms, timeZone) {
    if (!zoneFormats[timeZone]) {
      zoneFormats[timeZone] = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric'
      });
    }
    var parts = {};
    zoneFormats[timeZone].formatToParts(new Date(ms)).forEach(function(part) {
      parts[part.type] = toInt(part.value, 0);
    });
    // Some engines print midnight as hour 24.
    if (parts.hour === 24) {
      parts.hour = 0;
    }
    return parts;
  }

  function zoneOffset(ms, timeZone) {
    var p = zonedParts(ms, timeZone);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(ms / 1000) * 1000;
  }

  // The instant it is hour:00 on calendar `day` in timeZone. The second
  // offset lookup settles days when the clocks change.
  function zonedTime(day, hour, timeZone) {
    var wall = day * DAY_MS + hour * HOUR_MS;
    var guess = wall - zoneOffset(wall, timeZone);
    return wall - zoneOffset(guess, timeZone);
  }

  // config: the root's [data-ibp-ship-config] ({ timeZone, cutoffHour, closed }).
  // -> { daysAhead, shipDay, msLeft }: an order placed in the next msLeft ms
  // ships on shipDay, daysAhead calendar days after today in the warehouse's
  // time zone. null when the browser can't resolve the time zone.
  function shipCutoff(now, config) {
    var settings = config || {};
    var timeZone = settings.timeZone || SHIP_TIME_ZONE;
    var hour = Math.min(23, Math.max(0, toInt(settings.cutoffHour, SHIP_CUTOFF_HOUR)));
    var closed = closedDays(settings.closed);
    try {
      var p = zonedParts(now, timeZone);
      var today = dayNumber(p.year, p.month, p.day);
      for (var ahead = 0; ahead <= SHIP_LOOKAHEAD_DAYS; ahead += 1) {
        if (!isShipDay(today + ahead, closed)) {
          continue;
        }
        var deadline = zonedTime(today + ahead, hour, timeZone);
        if (deadline > now) {
          return { daysAhead: ahead, shipDay: today + ahead, msLeft: deadline - now };
        }
      }
    } catch (error) {}
    return null;
  }

  // 8040000 -> "2 hrs 14 mins". Rounds up, so it never reads "0 mins".
  function formatDuration(ms) {
    var minutes = Math.max(1, Math.ceil(ms / MINUTE_MS));
    var hours = Math.floor(minutes / 60);
    var rest = minutes % 60;
    var parts = [];
    if (hours) {
      parts.push(hours + (hours === 1 ? ' hr' : ' hrs'));
    }
    if (rest) {
      parts.push(rest + (rest === 1 ? ' min' : ' mins'));
    }
    return parts.join(' ');
  }

  function shipDayLabel(state) {
    if (state.daysAhead === 0) {
      return 'today';
    }
    if (state.daysAhead === 1) {
      return 'tomorrow';
    }
    var date = new Date(state.shipDay * DAY_MS);
    var name = WEEKDAYS[date.getUTCDay()];
    return state.daysAhead < 7 ? name : name + ', ' + MONTHS[date.getUTCMonth()] + ' ' + date.getUTCDate();
  }

  // Markup for the line, built only from numbers and fixed words. Within a
  // day of the cutoff it counts down; further out (Friday afternoon, the
  // weekend) a countdown in days means little, so it names the day instead.
  function shipMessage(state) {
    var day = '<strong class="ibp-ship__day">' + shipDayLabel(state) + '</strong>';
    if (state.msLeft > DAY_MS) {
      return 'Order now to ship ' + day;
    }
    return 'Order within <strong>' + formatDuration(state.msLeft) + '</strong> to ship ' + day;
  }

  // The stock half of the shipping line under the button: "In stock", or
  // "Only 4 left" at or under the inventory block's threshold.
  function shipStock(config, variantId) {
    var low = config.low ? config.low[variantId] : null;
    if (typeof low === 'number' && low > 0) {
      return '<strong class="ibp-ship__stock ibp-ship__stock--low">Only ' + low + ' left</strong>';
    }
    return '<strong class="ibp-ship__stock">In stock</strong>';
  }

  // Redraw just after the rounded-up minute count changes.
  function shipRefreshDelay(msLeft) {
    return (msLeft % MINUTE_MS || MINUTE_MS) + 250;
  }

  /*==========================================================================
    Controller: one per product section
  ==========================================================================*/

  // Where the breadcrumb's last crumb should go: the exact page the shopper
  // came from (its filters, sort and page) when that is the crumb's own
  // collection, else ''. A crumb with a query (a type crumb's ?filter.) is
  // left alone, since the page they left may be filtered differently.
  function crumbReturnUrl(referrer, href, origin) {
    if (!referrer || !href || href.indexOf('?') !== -1) {
      return '';
    }
    var from;
    var to;
    try {
      from = new URL(referrer);
      to = new URL(href, origin);
    } catch (e) {
      return '';
    }
    var path = function(url) {
      return url.pathname.replace(/\/+$/, '');
    };
    if (from.origin !== to.origin || path(from) !== path(to)) {
      return '';
    }
    return from.pathname + from.search;
  }

  function ProductPage(root) {
    this.root = root;
    this.listeners = [];
    this.observers = [];
    this.timers = {};
    this.variantId = root.getAttribute('data-ibp-variant-id') || '';
    var cart = parseJson(root.querySelector(SELECTORS.cartJson));
    this.cartQty = cartQuantities(cart);
    this.cartTotals = cartTotals(cart);
    // Displayed unit price in cents (salon price when data-salon-discount).
    this.unitPrice = toInt(root.getAttribute('data-ibp-unit-price'), 0);
    this.salon = root.getAttribute('data-salon-discount') === 'true';

    this.initChips();
    this.initOptionLabels();
    this.initQty();
    this.initInCart();
    this.initTable();
    this.initJump();
    this.initSticky();
    this.initShip();
    this.initStoreStock();
    this.initCrumbBack();
    this.bindShared();
    this.renderTotals();
  }

  ProductPage.prototype = {
    listen: function(target, type, handler) {
      if (!target || typeof target.addEventListener !== 'function') {
        return;
      }
      target.addEventListener(type, handler);
      this.listeners.push([target, type, handler]);
    },

    observe: function(target, options, callback) {
      if (!target || typeof window.MutationObserver !== 'function') {
        return;
      }
      var observer = new window.MutationObserver(callback);
      observer.observe(target, options);
      this.observers.push(observer);
    },

    later: function(name, fn, ms) {
      if (this.timers[name]) {
        window.clearTimeout(this.timers[name]);
      }
      var self = this;
      this.timers[name] = window.setTimeout(function() {
        self.timers[name] = null;
        fn();
      }, ms || 0);
    },

    destroy: function() {
      this.listeners.forEach(function(entry) {
        if (typeof entry[0].removeEventListener === 'function') {
          entry[0].removeEventListener(entry[1], entry[2]);
        }
      });
      this.listeners = [];
      this.observers.forEach(function(observer) {
        observer.disconnect();
      });
      this.observers = [];
      Object.keys(this.timers).forEach(function(name) {
        if (this.timers[name]) {
          window.clearTimeout(this.timers[name]);
        }
      }, this);
      this.timers = {};
      if (this.stickyVisible) {
        document.documentElement.classList.remove('ibp-sticky-on');
      }
    },

    bindShared: function() {
      var self = this;

      // theme.Variants dispatches this on the product container, before it
      // updates price/SKU, so read the variant from the event, never the DOM.
      this.listen(this.root, 'variantChange', function(evt) {
        self.onVariantChange(evt);
      });

      ['cart:updated', 'ajaxProduct:added'].forEach(function(type) {
        self.listen(document, type, function(evt) {
          self.onCart(evt);
        });
      });

      // Back/forward cache restores the page with the cart it had when the
      // shopper left, and a Back served from the HTTP cache rebuilds it from
      // the first response's cart JSON; re-read it so the counts are not stale.
      this.listen(window, 'pageshow', function(evt) {
        if (evt && evt.persisted) {
          self.refreshCart();
        }
      });
      if (historyLoad()) {
        this.refreshCart();
      }
    },

    onVariantChange: function(evt) {
      var variant = evt && evt.detail ? evt.detail.variant : null;
      this.variantId = variant && variant.id ? String(variant.id) : '';
      if (this.variantId) {
        this.root.setAttribute('data-ibp-variant-id', this.variantId);
      }
      if (variant && typeof variant.price === 'number') {
        this.unitPrice = this.salon ? Math.round(variant.price * 0.9) : variant.price;
      }
      this.updateBarcode(variant);
      this.updateOptionLabels(variant);
      this.renderInCart();
      this.renderShip();
      this.updateStickyVariant(variant);
      this.renderTotals();
      this.scheduleQtySync();
    },

    onCart: function(evt) {
      var cart = eventCart(evt);
      if (!cart) {
        return;
      }
      this.cartQty = cartQuantities(cart);
      this.cartTotals = cartTotals(cart);
      this.renderInCart();
      this.renderTableCart();
      this.scheduleQtySync();
    },

    refreshCart: function() {
      var theme = window.theme;
      if (!theme || !theme.cart || typeof theme.cart.getCart !== 'function' || challengeActive()) {
        return;
      }
      var self = this;
      theme.cart.getCart().then(function(cart) {
        if (cart && Array.isArray(cart.items)) {
          self.cartQty = cartQuantities(cart);
          self.cartTotals = cartTotals(cart);
          self.renderInCart();
          self.renderTableCart();
        }
      }, function() {});
    },

    /*------------------------------------------------------------------------
      Chips
    ------------------------------------------------------------------------*/

    initChips: function() {
      var self = this;
      toArray(this.root.querySelectorAll(SELECTORS.copy)).forEach(function(button, index) {
        self.listen(button, 'click', function() {
          self.copyChip(button, 'copied-' + index);
        });
      });
    },

    copyChip: function(button, timerName) {
      var chip = button.closest(SELECTORS.chip);
      var valueEl = chip && chip.querySelector(SELECTORS.chipValue);
      var value = valueEl ? String(valueEl.textContent || '').trim() : '';
      if (!value) {
        return;
      }
      var self = this;
      copyText(value, function(copied) {
        if (!copied) {
          return;
        }
        var label = chip.querySelector(SELECTORS.copied);
        chip.classList.add('is-copied');
        if (label) {
          label.hidden = false;
        }
        self.later(timerName || 'copied', function() {
          chip.classList.remove('is-copied');
          if (label) {
            label.hidden = true;
          }
        }, COPIED_MS);
      });
    },

    // Every copy: under the title (desktop) and under the buy area (phones).
    updateBarcode: function(variant) {
      var value = variant && variant.barcode ? String(variant.barcode) : '';
      toArray(this.root.querySelectorAll(SELECTORS.barcode)).forEach(function(chip) {
        var valueEl = chip.querySelector(SELECTORS.barcodeValue);
        if (valueEl) {
          valueEl.textContent = value;
        }
        chip.classList.toggle('hide', !value);
      });
    },

    /*------------------------------------------------------------------------
      Option labels: "Volume  20 Volume"
    ------------------------------------------------------------------------*/

    initOptionLabels: function() {
      var picker = this.root.querySelector(SELECTORS.picker);
      var self = this;
      this.optionLabels = [];
      if (!picker) {
        return;
      }
      toArray(picker.querySelectorAll(SELECTORS.variantWrapper)).forEach(function(wrapper) {
        var label = wrapper.querySelector(SELECTORS.variantLabel);
        var options = wrapper.querySelector(SELECTORS.optionWrap);
        // Colour swatches already print their value (variant__label-info).
        if (!label || !options || label.querySelector(SELECTORS.variantLabelInfo)) {
          return;
        }
        var index = toInt(String(options.getAttribute('data-index') || '').replace('option', ''), 0);
        if (!index) {
          return;
        }
        // Image swatches render theirs ("Selected: …") under the instruction.
        var value = label.querySelector(SELECTORS.optionValue);
        if (!value) {
          value = document.createElement('span');
          value.className = 'ibp-option-value';
          value.setAttribute('data-ibp-option-value', '');
          value.textContent = self.readOptionValue(options);
          label.appendChild(value);
        }
        self.optionLabels.push({ index: index, el: value });
      });
    },

    readOptionValue: function(options) {
      var checked = options.querySelector('input:checked');
      if (checked) {
        return checked.value || '';
      }
      var select = options.querySelector('select');
      return select ? (select.value || '') : '';
    },

    updateOptionLabels: function(variant) {
      if (!variant || !this.optionLabels) {
        return;
      }
      this.optionLabels.forEach(function(option) {
        var value = variant['option' + option.index];
        if (value !== null && value !== undefined) {
          option.el.textContent = String(value);
        }
      });
    },

    /*------------------------------------------------------------------------
      Quantity + line totals
    ------------------------------------------------------------------------*/

    initQty: function() {
      var self = this;
      this.mainQty = this.root.querySelector(SELECTORS.qtyInput);
      this.mainMinus = this.root.querySelector(SELECTORS.qtyMinus);
      this.mainPlus = this.root.querySelector(SELECTORS.qtyPlus);
      this.totals = toArray(this.root.querySelectorAll(SELECTORS.total));
      if (!this.mainQty) {
        return;
      }

      // Typed values are clamped by QtySelector's own change listener,
      // which may run after this one; its +/- buttons set the value
      // synchronously but fire no event.
      this.listen(this.mainQty, 'input', function() {
        self.scheduleQtySync();
      });
      this.listen(this.mainQty, 'change', function() {
        self.scheduleQtySync();
      });
      this.listen(this.mainMinus, 'click', function() {
        self.scheduleQtySync();
      });
      this.listen(this.mainPlus, 'click', function() {
        self.scheduleQtySync();
      });

      // The layout's updateQuantityLimit sets data-cartqty right before
      // updateATCBtn rewrites the value, so this sees the final value.
      this.observe(this.mainQty, {
        attributes: true,
        attributeFilter: ['data-cartqty', 'max', 'data-varid']
      }, function() {
        self.syncQty();
      });
      this.observe(this.mainQty.closest(SELECTORS.qtyWrapper), { attributes: true, attributeFilter: ['class'] }, function() {
        self.syncQty();
      });
      this.selectOnFocus(this.mainQty, 'qtySelect');
    },

    // Tapping a quantity selects it, so typing replaces the number. iOS
    // ignores a select() made during focus, so do it on the next task.
    selectOnFocus: function(input, name) {
      var self = this;
      this.listen(input, 'focus', function() {
        self.later(name, function() {
          if (input === document.activeElement && typeof input.setSelectionRange === 'function') {
            try {
              input.setSelectionRange(0, String(input.value).length);
            } catch (error) {}
          }
        }, 0);
      });
    },

    currentQty: function() {
      return this.mainQty ? Math.max(1, toInt(this.mainQty.value, 1)) : 1;
    },

    renderTotals: function() {
      if (!this.totals || !this.totals.length) {
        return;
      }
      // theme.Currency.formatMoney returns markup (superscript cents) built
      // only from the number and the shop's money format.
      var html = formatMoney(this.unitPrice * this.currentQty());
      var hide = this.unitPrice <= 0;
      this.totals.forEach(function(el) {
        el.innerHTML = html;
        el.hidden = hide;
      });
    },

    syncQty: function() {
      this.renderTotals();
      if (!this.mainQty || !this.stickyQtyValue) {
        return;
      }
      this.stickyQtyValue.textContent = String(Math.max(0, toInt(this.mainQty.value, 1)));
      var wrapper = this.mainQty.closest(SELECTORS.qtyWrapper);
      if (this.stickyPlus) {
        this.stickyPlus.classList.toggle('is-at-max', Boolean(wrapper && wrapper.classList.contains('is-at-max')));
      }
    },

    // AjaxProduct.success resets the quantity after the cart events fire,
    // so re-read on the next task.
    scheduleQtySync: function() {
      var self = this;
      this.later('qty', function() {
        self.syncQty();
      }, 0);
    },

    /*------------------------------------------------------------------------
      Already in your cart
    ------------------------------------------------------------------------*/

    initInCart: function() {
      this.inCart = this.root.querySelector(SELECTORS.inCart);
      this.inCartCount = this.inCart && this.inCart.querySelector(SELECTORS.inCartCount);

      var self = this;
      var view = this.inCart && this.inCart.querySelector(SELECTORS.inCartView);
      this.listen(view, 'click', function(evt) {
        self.openCartDrawer(evt, 'product-incart');
      });

      this.renderInCart();
    },

    // "View cart" links: only the drawer cart type has a drawer to open;
    // otherwise the link goes to /cart.
    openCartDrawer: function(evt, source) {
      var theme = window.theme;
      if (!theme || !theme.settings || theme.settings.cartType !== 'dropdown') {
        return;
      }
      evt.preventDefault();
      document.dispatchEvent(new CustomEvent('cart:open', {
        detail: { source: source }
      }));
    },

    renderInCart: function() {
      if (!this.inCart) {
        return;
      }
      var qty = this.variantId ? (this.cartQty[this.variantId] || 0) : 0;
      if (this.inCartCount) {
        this.inCartCount.textContent = String(qty);
      }
      this.inCart.hidden = qty <= 0;
    },

    /*------------------------------------------------------------------------
      Shipping cutoff
    ------------------------------------------------------------------------*/

    initShip: function() {
      this.shipLines = toArray(this.root.querySelectorAll(SELECTORS.ship));
      this.stockBlocks = toArray(this.root.querySelectorAll(SELECTORS.stockBlock));
      this.shipConfig = parseJson(this.root.querySelector(SELECTORS.shipConfig));
      if (!this.shipLines.length || !this.shipConfig) {
        this.shipConfig = null;
        return;
      }
      var self = this;
      // Background tabs throttle timers; catch up when the shopper returns.
      this.listen(document, 'visibilitychange', function() {
        if (!document.hidden) {
          self.renderShip();
        }
      });
      this.renderShip();
    },

    // Stofind's "Stock availability" button: the app adds it next to the
    // add-to-cart button after load. Checking a store's stock is a side step,
    // so it goes under the shipping line, still inside the form.
    initStoreStock: function() {
      var form = this.root.querySelector(SELECTORS.productForm);
      var anchor = form && form.querySelector(SELECTORS.ship);
      if (!anchor || !anchor.parentNode) {
        return;
      }
      var place = function() {
        var button = form.querySelector(SELECTORS.storeStock);
        if (button && anchor.nextSibling !== button) {
          anchor.parentNode.insertBefore(button, anchor.nextSibling);
        }
      };
      place();
      this.observe(form, { childList: true, subtree: true }, place);
    },

    // Came straight from the last crumb's collection: the crumb links to the
    // page as they left it, and a plain click steps back so the browser
    // restores the grid and scroll too. Only while nothing has added a
    // history entry since this page loaded (an in-page #anchor would); a
    // new-tab click, or a page opened in a new tab, just follows the link.
    initCrumbBack: function() {
      var link = this.root.querySelector(SELECTORS.crumbBack);
      var url = link && crumbReturnUrl(document.referrer, link.getAttribute('href'), window.location.origin);
      if (!url) {
        return;
      }
      link.setAttribute('href', url);
      var history = window.history;
      var length = history.length;
      var hash = window.location.hash;
      this.listen(link, 'click', function(evt) {
        if (evt.defaultPrevented || evt.button || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) {
          return;
        }
        if (length > 1 && history.length === length && window.location.hash === hash) {
          evt.preventDefault();
          history.back();
        }
      });
    },

    // Hidden for variants that are sold out or on backorder: they don't ship
    // from stock today.
    renderShip: function() {
      if (!this.shipConfig) {
        return;
      }
      var config = this.shipConfig;
      var variants = config.variants || {};
      var state = variants[this.variantId] === true ? shipCutoff(Date.now(), config) : null;
      var html = state ? shipMessage(state) : '';
      var stock = html && config.stock === true ? shipStock(config, this.variantId) : '';
      this.shipLines.forEach(function(line) {
        var text = line.querySelector(SELECTORS.shipText);
        if (text && html) {
          text.innerHTML = (stock && line.hasAttribute('data-ibp-ship-stock') ? stock + ' &middot; ' : '') + html;
        }
        line.hidden = !html;
      });
      // While the line says it's in stock, the inventory block would repeat
      // it; the block still shows sold-out and backordered variants.
      this.stockBlocks.forEach(function(block) {
        block.hidden = Boolean(stock);
      });
      if (state) {
        var self = this;
        this.later('ship', function() {
          self.renderShip();
        }, shipRefreshDelay(state.msLeft));
      }
    },

    /*------------------------------------------------------------------------
      Sticky buy bar
    ------------------------------------------------------------------------*/

    initSticky: function() {
      var bar = this.root.querySelector(SELECTORS.sticky);
      if (!bar) {
        return;
      }
      this.sticky = bar;
      this.stickyVisible = false;
      // Hidden until the first observation says where the button is.
      this.stickyState = { anchorInView: true, tableInView: false };
      this.atc = this.root.querySelector(SELECTORS.atc);
      this.stickyAnchor = this.root.querySelector(SELECTORS.stickyAnchor) || this.atc;

      this.initStickyButton();
      this.initStickyQty();
      this.initStickyPrice();
      this.initStickyVariant();
      this.initStickyVisibility();
      this.initPreviewOffset();
    },

    // Shopify's preview bar (theme previews) is fixed to the bottom too.
    initPreviewOffset: function() {
      var bar = this.sticky;
      var sync = function() {
        var preview = document.getElementById('PBarNextFrame');
        var shown = Boolean(preview) && String(window.location && window.location.search || '').indexOf('pb=0') === -1;
        if (bar.style && typeof bar.style.setProperty === 'function') {
          bar.style.setProperty('--ibp-preview-offset', shown ? '64px' : '0px');
        }
      };
      sync();
      this.listen(window, 'resize', sync);
      this.later('previewOffset', sync, 1500);
    },

    initStickyVisibility: function() {
      if (!this.stickyAnchor || typeof window.IntersectionObserver !== 'function') {
        return;
      }
      var self = this;
      var anchorObserver = new window.IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          // Whenever the real button (for guests, the sign-in card) is off
          // screen, above or below it: on phones it starts below the
          // gallery, so the bar is there from the start (Noah, 2026-09-26).
          self.stickyState.anchorInView = entry.isIntersecting;
        });
        self.renderStickyVisibility();
      });
      anchorObserver.observe(this.stickyAnchor);
      this.observers.push(anchorObserver);

      if (this.table) {
        // The table brings its own sticky footer once it is roughly a footer's
        // height into the viewport; hand over to it there, not at its top edge.
        var tableObserver = new window.IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            self.stickyState.tableInView = entry.isIntersecting;
          });
          self.renderStickyVisibility();
        }, { rootMargin: '0px 0px -120px 0px' });
        tableObserver.observe(this.table);
        this.observers.push(tableObserver);
      }
    },

    renderStickyVisibility: function() {
      var visible = Boolean(!this.stickyState.anchorInView && !this.stickyState.tableInView);
      if (visible === this.stickyVisible) {
        return;
      }
      this.stickyVisible = visible;
      this.sticky.classList.toggle('is-visible', visible);
      this.sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
      document.documentElement.classList.toggle('ibp-sticky-on', visible);
    },

    initStickyButton: function() {
      var button = this.sticky.querySelector(SELECTORS.stickyAdd);
      var atc = this.atc;
      if (!button || !atc) {
        return;
      }
      var self = this;
      this.stickyAdd = button;
      this.stickyAddText = button.querySelector(SELECTORS.stickyAddText);
      this.atcText = atc.querySelector(SELECTORS.atcText);
      this.form = atc.form || this.root.querySelector(SELECTORS.productForm);
      this.stickyLoading = false;

      this.syncStickyButton();

      // Mirror the real button: theme.Product and the layout's updateATCBtn
      // both write its disabled state and text.
      this.observe(atc, {
        attributes: true,
        attributeFilter: ['class', 'disabled'],
        childList: true,
        subtree: true,
        characterData: true
      }, function() {
        if (self.stickyLoading && !atc.classList.contains('btn--loading')) {
          self.finishStickyLoading();
          return;
        }
        self.syncStickyButton();
      });

      this.listen(button, 'click', function() {
        self.submitFromSticky();
      });

      ['ajaxProduct:added', 'ajaxProduct:error'].forEach(function(type) {
        self.listen(document, type, function(evt) {
          if (evt && evt.detail && evt.detail.addToCartBtn && evt.detail.addToCartBtn !== atc) {
            return;
          }
          self.finishStickyLoading();
        });
      });
    },

    atcDisabled: function() {
      var atc = this.atc;
      return Boolean(atc.disabled || atc.classList.contains('disabled') || atc.classList.contains('btn--loading'));
    },

    syncStickyButton: function() {
      if (!this.stickyAdd) {
        return;
      }
      var source = this.atcText ? this.atcText.textContent : this.atc.textContent;
      var text = String(source || '').trim();
      var disabled = this.atcDisabled();

      if (text && this.stickyAddText) {
        this.stickyAddText.textContent = text;
      }
      if (text) {
        this.stickyAdd.setAttribute('aria-label', text);
      }
      this.stickyAdd.classList.toggle('disabled', disabled);
      if (!this.stickyLoading) {
        this.stickyAdd.disabled = disabled;
      }
    },

    submitFromSticky: function() {
      this.syncStickyButton();
      if (!this.form || this.atcDisabled() || this.stickyAdd.disabled) {
        return;
      }
      this.setStickyLoading();
      try {
        // requestSubmit runs the form's submit listeners: theme.AjaxProduct's
        // access gate and add.
        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit(this.atc);
        } else {
          this.atc.click();
        }
      } catch (error) {
        this.finishStickyLoading();
        throw error;
      }
    },

    setStickyLoading: function() {
      var self = this;
      this.stickyLoading = true;
      this.stickyAdd.classList.add('is-loading');
      this.stickyAdd.disabled = true;
      this.stickyAdd.setAttribute('aria-busy', 'true');
      this.later('stickyRecovery', function() {
        self.finishStickyLoading();
      }, STICKY_RECOVERY_MS);
    },

    finishStickyLoading: function() {
      if (!this.stickyAdd) {
        return;
      }
      if (this.timers.stickyRecovery) {
        window.clearTimeout(this.timers.stickyRecovery);
        this.timers.stickyRecovery = null;
      }
      this.stickyLoading = false;
      this.stickyAdd.classList.remove('is-loading');
      this.stickyAdd.setAttribute('aria-busy', 'false');
      this.syncStickyButton();
    },

    initStickyQty: function() {
      var wrap = this.sticky.querySelector(SELECTORS.stickyQty);
      if (!wrap) {
        return;
      }
      if (!this.mainQty || !this.mainMinus || !this.mainPlus) {
        wrap.hidden = true;
        return;
      }

      var self = this;
      this.stickyQtyValue = wrap.querySelector(SELECTORS.stickyQtyValue);
      this.stickyPlus = wrap.querySelector(SELECTORS.stickyPlus);

      // Click the real buttons so theme.QtySelector's max, cart-quantity and
      // "only N more" toast all apply; it sets the value synchronously.
      this.listen(wrap.querySelector(SELECTORS.stickyMinus), 'click', function() {
        self.mainMinus.click();
        self.syncQty();
      });
      this.listen(this.stickyPlus, 'click', function() {
        self.mainPlus.click();
        self.syncQty();
      });

      this.syncQty();
    },

    initStickyPrice: function() {
      var source = this.root.querySelector(SELECTORS.price);
      var target = this.sticky.querySelector(SELECTORS.stickyPrice);
      // Layouts without a price block (the gift card template) have nothing
      // to mirror: updateStickyVariant prices the bar from the variant.
      this.stickyPriceFromVariant = Boolean(target && !source);
      this.stickyPrice = target;
      if (!source || !target) {
        return;
      }
      var sync = function() {
        target.innerHTML = source.innerHTML;
      };
      sync();
      this.observe(source, { childList: true, subtree: true, characterData: true }, sync);
    },

    initStickyVariant: function() {
      var button = this.sticky.querySelector(SELECTORS.stickyVariant);
      var picker = this.root.querySelector(SELECTORS.picker);
      if (!button) {
        return;
      }
      this.stickyVariantText = button.querySelector(SELECTORS.stickyVariantText);
      this.stickyThumb = this.sticky.querySelector(SELECTORS.stickyThumb);
      this.listen(button, 'click', function() {
        if (!picker) {
          return;
        }
        if (typeof picker.scrollIntoView === 'function') {
          picker.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
        }
        var focusTarget = picker.querySelector('input:checked') || picker.querySelector('select');
        if (focusTarget && typeof focusTarget.focus === 'function') {
          focusTarget.focus({ preventScroll: true });
        }
      });
    },

    updateStickyVariant: function(variant) {
      if (!this.sticky || !variant) {
        return;
      }
      if (this.stickyVariantText) {
        this.stickyVariantText.textContent = variant.title || '';
      }
      if (this.stickyPriceFromVariant && typeof variant.price === 'number') {
        this.stickyPrice.innerHTML = formatMoney(this.unitPrice);
      }
      var thumb = this.stickyThumb || this.sticky.querySelector(SELECTORS.stickyThumb);
      var image = variant.featured_image && variant.featured_image.src;
      if (thumb && image) {
        thumb.setAttribute('src', imageUrl(image, 120));
      }
    },

    /*------------------------------------------------------------------------
      Multi-variant order table
    ------------------------------------------------------------------------*/

    initTable: function() {
      var table = this.root.querySelector(SELECTORS.table);
      if (!table) {
        return;
      }
      var self = this;
      this.table = table;
      this.tableFooter = table.querySelector(SELECTORS.tableFooter);
      this.tableCount = table.querySelector(SELECTORS.tableCount);
      this.tableSubtotal = table.querySelector(SELECTORS.tableSubtotal);
      this.tableEmpty = table.querySelector(SELECTORS.tableEmpty);

      this.rows = toArray(table.querySelectorAll(SELECTORS.row)).map(function(el) {
        return {
          el: el,
          id: el.getAttribute('data-variant-id'),
          search: String(el.getAttribute('data-ibp-search') || '').toLowerCase(),
          input: el.querySelector(SELECTORS.rowInput)
        };
      });

      // collection-atc.js sets the stepper values on click without firing an
      // event; highlight rows from what they show once its handler has run.
      var markLater = function() {
        self.later('tableRows', function() {
          self.markRows();
        }, 0);
      };
      this.listen(table, 'click', markLater);
      this.listen(table, 'change', markLater);

      // Enter / Done leaves the field (the change commits the quantity), so
      // the next tap selects again instead of adding digits after it.
      this.rows.forEach(function(row) {
        self.listen(row.input, 'keydown', function(evt) {
          if ((evt.key === 'Enter' || evt.keyCode === 13) && typeof row.input.blur === 'function') {
            row.input.blur();
          }
        });
        self.selectOnFocus(row.input, 'tableSelect');
      });

      this.listen(table.querySelector(SELECTORS.tableFilter), 'input', function(evt) {
        self.filterRows(evt.target ? evt.target.value : '');
      });
      this.listen(table.querySelector(SELECTORS.tableView), 'click', function(evt) {
        self.openCartDrawer(evt, 'product-variant-table');
      });

      // The theme editor re-renders the section after collection-atc.js has
      // initialised: bind the new steppers (already-bound ones are skipped).
      var atc = window.CollectionATC;
      if (atc && typeof atc.rebind === 'function') {
        var binding = atc.rebind(table);
        if (binding && typeof binding.then === 'function') {
          binding.then(null, function() {});
        }
      }

      this.renderTableCart();
    },

    // "Ordering several? Use the bulk order table": glide to the table instead
    // of jumping (instant for reduced motion), and move focus to its heading
    // so keyboard users land there too. The table's scroll-margin-top keeps
    // it clear of the sticky header.
    initJump: function() {
      var link = this.root.querySelector(SELECTORS.jump);
      var table = this.table;
      if (!link || !table) {
        return;
      }
      this.listen(link, 'click', function(evt) {
        if (typeof table.scrollIntoView !== 'function') {
          return;
        }
        evt.preventDefault();
        table.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
        var title = table.querySelector(SELECTORS.tableTitle);
        if (title && typeof title.focus === 'function') {
          title.focus({ preventScroll: true });
        }
      });
    },

    markRows: function() {
      (this.rows || []).forEach(function(row) {
        if (row.input) {
          row.el.classList.toggle('is-selected', toInt(row.input.value, 0) > 0);
        }
      });
    },

    // From the cart itself: rows already in it, and the summary bar.
    renderTableCart: function() {
      if (!this.table) {
        return;
      }
      var quantities = this.cartQty;
      var ids = this.rows.map(function(row) {
        row.el.classList.toggle('is-selected', (quantities[row.id] || 0) > 0);
        return row.id;
      });
      var summary = cartSummary(ids, quantities, this.cartTotals);
      if (this.tableCount) {
        this.tableCount.textContent = String(summary.count);
      }
      if (this.tableSubtotal) {
        this.tableSubtotal.innerHTML = formatMoney(summary.subtotal);
      }
      if (this.tableFooter) {
        this.tableFooter.hidden = summary.count <= 0;
      }
      this.table.classList.toggle('has-items', summary.count > 0);
    },

    filterRows: function(term) {
      var needle = String(term || '').trim().toLowerCase();
      var visible = 0;
      (this.rows || []).forEach(function(row) {
        var match = !needle || row.search.indexOf(needle) !== -1;
        row.el.hidden = !match;
        if (match) {
          visible += 1;
        }
      });
      if (this.tableEmpty) {
        this.tableEmpty.hidden = visible > 0;
      }
    }
  };

  /*==========================================================================
    Boot
  ==========================================================================*/

  var instances = [];

  function isInside(node, scope) {
    while (node) {
      if (node === scope) {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  }

  function boot(scope) {
    var container = scope || document;
    toArray(container.querySelectorAll(SELECTORS.root)).forEach(function(root) {
      var known = instances.some(function(instance) {
        return instance.root === root;
      });
      if (!known) {
        instances.push(new ProductPage(root));
      }
    });
  }

  function teardown(scope) {
    instances = instances.filter(function(instance) {
      if (scope && isInside(instance.root, scope)) {
        instance.destroy();
        return false;
      }
      return true;
    });
  }

  window.IBProductPage = {
    cartQuantities: cartQuantities,
    cartTotals: cartTotals,
    cartSummary: cartSummary,
    formatMoney: formatMoney,
    imageUrl: imageUrl,
    shipCutoff: shipCutoff,
    shipMessage: shipMessage,
    shipHolidays: holidays,
    formatDuration: formatDuration,
    crumbReturnUrl: crumbReturnUrl,
    ProductPage: ProductPage,
    boot: boot,
    teardown: teardown,
    instances: function() {
      return instances.slice();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      boot();
    });
  } else {
    boot();
  }

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function(evt) {
    boot(evt.target);
  });
  document.addEventListener('shopify:section:unload', function(evt) {
    teardown(evt.target);
  });
})();
